import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
  Button,
  FormControl,
  FormLabel,
  Flex,
  Image,
  Icon,
  SimpleGrid,
  useToast,
  keyframes,
} from '@chakra-ui/react';
import { FaArrowRight, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { Calendar } from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import { submitLandingForm } from '../services/landingService';
import { logger } from '../utils/logger';

const BRAND = {
  heroBg: 'linear-gradient(135deg, #0a1835 0%, #0d2a5a 50%, #0f3a78 100%)',
  primary: '#1D6EF5',
  ink: '#0A1F44',
};

const logoFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  border: none;
  background: transparent;

  .ant-picker-calendar-header {
    padding: 0 0 12px;
  }

  .ant-picker-content th {
    color: #718096;
    font-size: 12px;
    font-weight: 600;
  }

  .ant-picker-cell-inner {
    border-radius: 10px;
  }

  .ant-picker-cell-selected .ant-picker-cell-inner {
    background: #1d6ef5 !important;
  }

  .ant-picker-cell-today .ant-picker-cell-inner::before {
    border-color: #38bdf8 !important;
  }
`;

const TIME_SLOTS = [
  '9:00 AM',
  '9:30 AM',
  '10:00 AM',
  '10:30 AM',
  '11:00 AM',
  '11:30 AM',
  '12:00 PM',
  '12:30 PM',
  '1:00 PM',
  '1:30 PM',
  '2:00 PM',
  '2:30 PM',
  '3:00 PM',
  '3:30 PM',
  '4:00 PM',
  '4:30 PM',
];

const STEPS = [
  { key: 'url', label: 'Prospect URL' },
  { key: 'contact', label: 'Your details' },
  { key: 'schedule', label: 'Pick a time' },
];

const inputStyles = {
  size: 'lg',
  bg: 'white',
  border: '1px solid',
  borderColor: 'gray.200',
  borderRadius: 'xl',
  _hover: { borderColor: 'gray.300' },
  _focus: {
    borderColor: 'brand.500',
    boxShadow: '0 0 0 3px rgba(0, 102, 255, 0.12)',
  },
  transition: 'all 0.2s',
};

function normalizeUrl(value) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isValidUrl(value) {
  try {
    const url = new URL(normalizeUrl(value));
    return Boolean(url.hostname && url.hostname.includes('.'));
  } catch {
    return false;
  }
}

function isSelectableDate(date) {
  const day = moment(date);
  const isWeekday = day.isoWeekday() <= 5;
  const isFuture = day.isSameOrAfter(moment().startOf('day'));
  return isWeekday && isFuture;
}

function DemoPage() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    prospectUrl: '',
    name: '',
    email: '',
    agencyUrl: '',
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const toast = useToast();

  const calendarValue = useMemo(
    () => (selectedDate ? moment(selectedDate) : moment()),
    [selectedDate],
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const showToast = (title, status, description) => {
    toast({
      title,
      description,
      status,
      duration: 3000,
      isClosable: true,
    });
  };

  const goToContactStep = () => {
    if (!isValidUrl(formData.prospectUrl)) {
      showToast('Enter a valid prospect URL.', 'warning');
      return;
    }
    setStep(1);
  };

  const goToScheduleStep = () => {
    if (!formData.name.trim() || !formData.email.trim() || !isValidUrl(formData.agencyUrl)) {
      showToast('Enter your name, email, and a valid agency URL.', 'warning');
      return;
    }
    if (!formData.email.includes('@')) {
      showToast('Enter a valid email address.', 'warning');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      showToast('Select a date and time for your demo.', 'warning');
      return;
    }

    const scheduledTime = `${moment(selectedDate).format('dddd, MMMM D, YYYY')} at ${selectedTime}`;

    setIsSubmitting(true);
    try {
      await submitLandingForm(
        formData.email,
        'demo-booking',
        formData.name.trim(),
        null,
        {
          prospectUrl: normalizeUrl(formData.prospectUrl),
          agencyUrl: normalizeUrl(formData.agencyUrl),
          scheduledTime,
        },
      );
      setIsSubmitted(true);
      showToast('Demo booked!', 'success', "We'll confirm your time shortly.");
    } catch (err) {
      logger.error('Demo funnel submission error:', err);
      showToast('Something went wrong.', 'error', 'Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box minH="100vh" bg={BRAND.heroBg} position="relative" overflow="hidden">
      <Helmet>
        <title>Book a Demo | Localytics</title>
        <meta
          name="description"
          content="Book a live demo with the Localytics team. Enter a prospect URL, your details, and pick a time."
        />
      </Helmet>

      <Box
        position="absolute"
        top="-20%"
        right="-10%"
        w={{ base: '280px', md: '520px' }}
        h={{ base: '280px', md: '520px' }}
        borderRadius="full"
        bg="radial-gradient(circle, rgba(29,110,245,0.2) 0%, transparent 70%)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-15%"
        left="-8%"
        w={{ base: '240px', md: '460px' }}
        h={{ base: '240px', md: '460px' }}
        borderRadius="full"
        bg="radial-gradient(circle, rgba(59,166,245,0.14) 0%, transparent 70%)"
        pointerEvents="none"
      />

      <Container maxW="lg" position="relative" zIndex={1} py={{ base: 8, md: 12 }}>
        <VStack spacing={{ base: 8, md: 10 }} align="stretch">
          <Flex justify="center">
            <Image
              src="/output.png"
              alt="Localytics"
              h={{ base: '48px', md: '64px' }}
              w="auto"
              objectFit="contain"
              filter="brightness(0) invert(1)"
              animation={`${logoFloat} 4s ease-in-out infinite`}
            />
          </Flex>

          {!isSubmitted && (
            <HStack spacing={3} justify="center" align="center">
              {STEPS.map((item, index) => (
                <React.Fragment key={item.key}>
                  <HStack spacing={2} align="center" flexShrink={0}>
                    <Flex
                      w="28px"
                      h="28px"
                      flexShrink={0}
                      borderRadius="full"
                      align="center"
                      justify="center"
                      fontSize="xs"
                      fontWeight="bold"
                      lineHeight="1"
                      bg={index <= step ? 'white' : 'whiteAlpha.200'}
                      color={index <= step ? BRAND.ink : 'whiteAlpha.800'}
                    >
                      {index + 1}
                    </Flex>
                    <Text
                      as="span"
                      fontSize="sm"
                      lineHeight="1"
                      m={0}
                      whiteSpace="nowrap"
                      color={index === step ? 'white' : 'whiteAlpha.700'}
                      fontWeight="semibold"
                      display={{ base: 'none', sm: 'inline-flex' }}
                      alignItems="center"
                    >
                      {item.label}
                    </Text>
                  </HStack>
                  {index < STEPS.length - 1 && (
                    <Box
                      w="24px"
                      h="1px"
                      flexShrink={0}
                      bg="whiteAlpha.300"
                      display={{ base: 'none', sm: 'block' }}
                    />
                  )}
                </React.Fragment>
              ))}
            </HStack>
          )}

          <Box
            bg="white"
            borderRadius="2xl"
            border="1px solid"
            borderColor="whiteAlpha.300"
            boxShadow="0 24px 64px rgba(10, 31, 68, 0.28)"
            px={{ base: 6, md: 8 }}
            py={{ base: 7, md: 9 }}
          >
            {isSubmitted ? (
              <VStack spacing={5} py={4} textAlign="center">
                <Flex
                  w="64px"
                  h="64px"
                  borderRadius="full"
                  bg="green.50"
                  align="center"
                  justify="center"
                >
                  <Icon as={FaCheckCircle} color="green.500" boxSize={8} />
                </Flex>
                <VStack spacing={2}>
                  <Heading size="md" color="gray.900">
                    You&apos;re booked!
                  </Heading>
                  <Text color="gray.600" maxW="360px">
                    We&apos;ll confirm your demo time and send prep details for the prospect URL you shared.
                  </Text>
                </VStack>
              </VStack>
            ) : (
              <VStack spacing={6} align="stretch">
                {step === 0 && (
                  <>
                    <VStack spacing={2} align="stretch" textAlign="center">
                      <Heading
                        as="h1"
                        fontSize={{ base: '3xl', md: '4xl' }}
                        color="gray.900"
                        letterSpacing="-0.03em"
                        lineHeight={1.1}
                      >
                        Enter Prospect&apos;s URL
                      </Heading>
                      <Text color="gray.500" fontSize="md">
                        We&apos;ll tailor the demo around the business you want to show.
                      </Text>
                    </VStack>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                        Prospect URL
                      </FormLabel>
                      <Input
                        name="prospectUrl"
                        type="url"
                        placeholder="https://prospect-business.com"
                        value={formData.prospectUrl}
                        onChange={handleChange}
                        onKeyDown={(e) => e.key === 'Enter' && goToContactStep()}
                        {...inputStyles}
                      />
                    </FormControl>
                    <Button
                      size="lg"
                      w="full"
                      py={7}
                      borderRadius="xl"
                      color="white"
                      bg="brand.500"
                      rightIcon={<FaArrowRight />}
                      onClick={goToContactStep}
                      _hover={{ bg: 'brand.600' }}
                    >
                      Continue
                    </Button>
                  </>
                )}

                {step === 1 && (
                  <>
                    <VStack spacing={2} align="stretch" textAlign="center">
                      <Heading size="lg" color="gray.900" letterSpacing="-0.02em">
                        Your details
                      </Heading>
                      <Text color="gray.500" fontSize="md">
                        Tell us who to send the calendar invite to.
                      </Text>
                    </VStack>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                        Name
                      </FormLabel>
                      <Input
                        name="name"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={handleChange}
                        {...inputStyles}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                        Email
                      </FormLabel>
                      <Input
                        name="email"
                        type="email"
                        placeholder="you@agency.com"
                        value={formData.email}
                        onChange={handleChange}
                        {...inputStyles}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="semibold" color="gray.700">
                        Agency URL
                      </FormLabel>
                      <Input
                        name="agencyUrl"
                        type="url"
                        placeholder="https://your-agency.com"
                        value={formData.agencyUrl}
                        onChange={handleChange}
                        {...inputStyles}
                      />
                    </FormControl>
                    <HStack spacing={3}>
                      <Button
                        size="lg"
                        flex="1"
                        variant="outline"
                        leftIcon={<FaArrowLeft />}
                        onClick={() => setStep(0)}
                      >
                        Back
                      </Button>
                      <Button
                        size="lg"
                        flex="2"
                        color="white"
                        bg="brand.500"
                        rightIcon={<FaArrowRight />}
                        onClick={goToScheduleStep}
                        _hover={{ bg: 'brand.600' }}
                      >
                        Continue
                      </Button>
                    </HStack>
                  </>
                )}

                {step === 2 && (
                  <>
                    <VStack spacing={2} align="stretch" textAlign="center">
                      <Heading size="lg" color="gray.900" letterSpacing="-0.02em">
                        Pick a time
                      </Heading>
                      <Text color="gray.500" fontSize="md">
                        Choose a weekday that works for your demo.
                      </Text>
                    </VStack>

                    <Box
                      border="1px solid"
                      borderColor="gray.100"
                      borderRadius="xl"
                      p={{ base: 3, md: 4 }}
                      bg="gray.50"
                    >
                      <StyledCalendar
                        fullscreen={false}
                        value={calendarValue}
                        onSelect={(date) => {
                          if (isSelectableDate(date)) {
                            setSelectedDate(date.toDate());
                          }
                        }}
                        disabledDate={(date) => !isSelectableDate(date)}
                      />
                    </Box>

                    {selectedDate && (
                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                          Available times for {moment(selectedDate).format('dddd, MMM D')}
                        </Text>
                        <SimpleGrid columns={{ base: 2, sm: 3 }} spacing={2}>
                          {TIME_SLOTS.map((slot) => {
                            const isSelected = selectedTime === slot;
                            return (
                              <Button
                                key={slot}
                                size="sm"
                                variant={isSelected ? 'solid' : 'outline'}
                                colorScheme={isSelected ? 'blue' : 'gray'}
                                borderRadius="lg"
                                onClick={() => setSelectedTime(slot)}
                              >
                                {slot}
                              </Button>
                            );
                          })}
                        </SimpleGrid>
                      </Box>
                    )}

                    <HStack spacing={3}>
                      <Button
                        size="lg"
                        flex="1"
                        variant="outline"
                        leftIcon={<FaArrowLeft />}
                        onClick={() => setStep(1)}
                      >
                        Back
                      </Button>
                      <Button
                        size="lg"
                        flex="2"
                        color="white"
                        bg="brand.500"
                        rightIcon={<FaArrowRight />}
                        onClick={handleSubmit}
                        isLoading={isSubmitting}
                        loadingText="Booking..."
                        _hover={{ bg: 'brand.600' }}
                      >
                        Book demo
                      </Button>
                    </HStack>
                  </>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}

export default DemoPage;
